import { forwardRef } from 'react';
import { cn } from '@/utils';

export interface SelectOption {
  value: string;
  label: string;
  /**
   * M-03: an `<optgroup>` heading to file this option under. Options are
   * rendered in the order given and consecutive options sharing a group are
   * wrapped together, so a list can be part flat and part grouped -- which is
   * what the quote status filter needs ("Older statuses" sits below the live
   * ones). Omitted on every existing caller, which therefore renders exactly
   * as it did before.
   */
  group?: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * Split options into consecutive runs sharing a `group`, preserving order.
 * Ungrouped options come back in a run with `group: undefined` and are emitted
 * bare, so a list with no groups at all produces exactly one run and the same
 * markup this component always rendered.
 */
function groupOptions(options: SelectOption[]): { group?: string; options: SelectOption[] }[] {
  const runs: { group?: string; options: SelectOption[] }[] = [];
  for (const option of options) {
    const last = runs[runs.length - 1];
    if (last && last.group === option.group) {
      last.options.push(option);
    } else {
      runs.push({ group: option.group, options: [option] });
    }
  }
  return runs;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, helperText, options, placeholder, id, ...props }, ref) => {
    const selectId = id || props.name;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
            {props.required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'w-full pl-3 pr-10 py-2 border rounded-lg transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent',
            'bg-white dark:bg-gray-700 dark:text-gray-100 appearance-none cursor-pointer select-chevron',
            error
              ? 'border-red-300 focus:ring-red-500 dark:border-red-700 dark:focus:ring-red-400'
              : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {groupOptions(options).map((run, runIndex) =>
            run.group ? (
              <optgroup key={`group-${runIndex}-${run.group}`} label={run.group}>
                {run.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              run.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            )
          )}
        </select>
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
